import { filterConsolidatedBranches } from "./consolidatedPillarSelection";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Avatar from "@mui/material/Avatar";
import { green, red, yellow } from "@mui/material/colors";
import Dialog from "components/NoScrollDialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import confidentialSticker from "assets/images/confidential.png";
import ifpcSticker from "assets/images/ifpc-cover.jpg";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ResizableTextarea from "components/ResizableTextarea";
import { INTERNAL } from "const";
import { useLanguage } from "contexts/language.context";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import auditsService from "services/audits-service";
import branchesService from "services/branches-service";
import { debounce } from "utils/debounce";
import {
  colors,
  flagColor,
  flagColorBasedOnString,
  flagHexColor,
  perfs,
} from "layouts/audits/components/utils";
import downloadPdf from "./GenerateReport";
import styles from "./consolidatedReportStyles.module.css";
import { Box } from "@mui/material";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const safeNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

const normalizeReportGenerated = (reportGenerated = {}) => {
  const table1Pillars = reportGenerated.table1Pillars || reportGenerated.table1;
  const table2Pillars = reportGenerated.table2Pillars || reportGenerated.table2;

  return {
    ...reportGenerated,
    table1Pillars,
    table2Pillars,
    table1: table1Pillars,
    table2: table2Pillars,
  };
};

const normalizeAudit = (audit) => ({
  ...audit,
  reportGenerated: normalizeReportGenerated(audit?.reportGenerated),
});

const normalizeBranches = (branches = []) =>
  branches.map((branch) => ({
    ...branch,
    audits: (branch.audits || []).map(normalizeAudit),
  }));

const getAuditTotalPercentage = (audit) =>
  safeNumber(
    normalizeReportGenerated(audit?.reportGenerated).table1Pillars?.at(-1)
      ?.totalPercentage,
  );

const getBranchStandaloneTotalPercentage = (branch) => {
  if (!branch?.audits?.length) return 0;

  return (
    branch.audits.reduce(
      (total, currentAudit) => total + getAuditTotalPercentage(currentAudit),
      0,
    ) / branch.audits.length
  );
};

const getGroupStandaloneTotalPercentage = (groupBranches) => {
  if (!groupBranches?.length) return 0;

  return (
    groupBranches.reduce(
      (total, branch) => total + getBranchStandaloneTotalPercentage(branch),
      0,
    ) / groupBranches.length
  );
};

const CustomizedMenus = ({
  branchGroup,
  loading,
  setLoading,
  selected,
  selectedPillarIds,
  type,
}) => {
  const { label } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGenerate = async () => {
    handleClose();
    setLoading(true);
    await downloadPdf(`${branchGroup.name}-CR-ifpc360`, "consolidated");
    setLoading(false);
  };

  async function downloadPDF(url, filename) {
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  const handleGenerateLink = async () => {
    handleClose();
    setLoading(true);
    const [response, data] = await branchesService.getConsolidatedReportLink(
      { branchGroupId: branchGroup.id },
      { type, selected, selectedPillarIds },
    );
    if (response.ok) {
      await downloadPDF(data.url, `${branchGroup.name}-CR-ifpc360.pdf`);
    }
    setLoading(false);
  };

  return (
    <div>
      <LoadingButton
        loading={loading}
        type="button"
        variant="gradient"
        color="info"
        id="demo-customized-button"
        aria-controls={open ? "demo-customized-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disableElevation
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {label("options-button")}
      </LoadingButton>
      <Menu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {/* <MenuItem
          sx={{
            p: 1,
            px: 2,
            m: 0,
            mx: -1,
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}
          onClick={handleGenerate}
          disableRipple
        >
          <FileDownloadIcon />
          {label("download-pdf-button")}
        </MenuItem> */}

        <MenuItem
          sx={{
            p: 1,
            px: 2,
            m: 0,
            mx: -1,
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}
          onClick={handleGenerateLink}
          disableRipple
        >
          <FileDownloadIcon />
          {label("download-pdf-button")}
          {/* <MDBadge badgeContent="BETA" color="primary" /> */}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default function AuditsConsolidatedReportGeneration({ isOpen, data }) {
  let branchGroup = data.branchGroup;
  let branches = normalizeBranches(
    filterConsolidatedBranches(data.branches, data.selectedPillarIds),
  );
  let selected = data.selected;
  let type = data.auditsReport;
  let audit;

  let isComparative = false;
  {
    branches = branches.filter(
      (branch) => branch.audits[branch.audits.length - 1],
    );
    switch (type.value) {
      case "normal":
        break;
      case "comparative":
        isComparative = true;
        branches = branches.map((branch) => ({
          ...branch,
          audits: [
            branch.audits.find((audit) => audit.type == INTERNAL),
            branch.audits.find((audit) => audit.type != INTERNAL),
          ],
        }));
        break;
      case "region":
        branches = branches.filter(
          (branch) => branch.region.id == type.region.id,
        );
        break;
      case "comprehensive":
        if (type.areas) {
          branches = type.areas
            .filter((area) =>
              branches.find((branch) => branch.areaId == area.id),
            )
            .map((area) => {
              let audits = {};
              const areaBranches = branches.filter(
                (branch) => branch.areaId == area.id,
              );

              branches.map((branch) => {
                if (area.id != branch.areaId) return;
                const auditGroup = branch.audits;

                auditGroup.map((audit) => {
                  const month = monthNames[new Date(audit.date).getMonth()];
                  if (!audits[month]) audits[month] = [];

                  audits[month].push(audit);
                });
              });

              audits = Object.keys(audits).map((key) => {
                const auditGroup = audits[key];
                if (auditGroup.length == 1) return auditGroup[0];

                const mergedAudit = {
                  id: key,
                  date: auditGroup[0].date,
                  reportGenerated: { table1: {}, table2: {} },
                };

                auditGroup.map((audit) => {
                  audit.reportGenerated.table2.map((row) => {
                    if (!mergedAudit.reportGenerated.table2[row.pillarId])
                      mergedAudit.reportGenerated.table2[row.pillarId] = {
                        pillarId: row.pillarId,
                        description: row.description,
                        referenceCode: row.referenceCode,
                        totals: { dev: 0, na: 0, std: 0, weight: 0 },
                      };
                    mergedAudit.reportGenerated.table2[
                      row.pillarId
                    ].totals.dev += row.totals.dev;
                    mergedAudit.reportGenerated.table2[
                      row.pillarId
                    ].totals.na += row.totals.na;
                    mergedAudit.reportGenerated.table2[
                      row.pillarId
                    ].totals.std += row.totals.std;
                    mergedAudit.reportGenerated.table2[
                      row.pillarId
                    ].totals.weight += row.totals.weight;
                  });

                  audit.reportGenerated.table1.map((row, index) => {
                    const isLast =
                      index == audit.reportGenerated.table1.length - 1;

                    if (!isLast) {
                      if (!mergedAudit.reportGenerated.table1[row.pillarId]) {
                        mergedAudit.reportGenerated.table1[row.pillarId] = {
                          pillarId: row.pillarId,
                          description: row.description,
                          referenceCode: row.referenceCode,
                          devPoints: 0,
                          naPoints: 0,
                          score: 0,
                          stdPoints: 0,
                          weight: 0,
                        };
                      }

                      mergedAudit.reportGenerated.table1[
                        row.pillarId
                      ].devPoints += row.devPoints;
                      mergedAudit.reportGenerated.table1[
                        row.pillarId
                      ].naPoints += row.naPoints;
                      mergedAudit.reportGenerated.table1[row.pillarId].score +=
                        row.score;
                      mergedAudit.reportGenerated.table1[
                        row.pillarId
                      ].stdPoints += row.stdPoints;
                      mergedAudit.reportGenerated.table1[row.pillarId].weight +=
                        row.weight;
                    } else {
                      if (!mergedAudit.reportGenerated.table1["lastRow"]) {
                        mergedAudit.reportGenerated.table1["lastRow"] = {
                          devPercentage: 0,
                          devTotal: 0,
                          naPercentage: 0,
                          naTotal: 0,
                          stdPercentage: 0,
                          stdTotal: 0,
                          totalPercentage: 0,
                          totalPoints: 0,
                        };
                      }

                      mergedAudit.reportGenerated.table1[
                        "lastRow"
                      ].devPercentage += row.devPercentage;
                      mergedAudit.reportGenerated.table1["lastRow"].devTotal +=
                        row.devTotal;
                      mergedAudit.reportGenerated.table1[
                        "lastRow"
                      ].naPercentage += row.naPercentage;
                      mergedAudit.reportGenerated.table1["lastRow"].naTotal +=
                        row.naTotal;
                      mergedAudit.reportGenerated.table1[
                        "lastRow"
                      ].stdPercentage += row.stdPercentage;
                      mergedAudit.reportGenerated.table1["lastRow"].stdTotal +=
                        row.stdTotal;
                      mergedAudit.reportGenerated.table1[
                        "lastRow"
                      ].totalPercentage += row.totalPercentage;
                      mergedAudit.reportGenerated.table1[
                        "lastRow"
                      ].totalPoints += row.totalPoints;
                    }
                  });
                });

                Object.keys(mergedAudit.reportGenerated.table2).map((key) => {
                  mergedAudit.reportGenerated.table2[key].totals.dev /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table2[key].totals.na /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table2[key].totals.std /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table2[key].totals.weight /=
                    auditGroup.length;
                });

                Object.keys(mergedAudit.reportGenerated.table1).map((key) => {
                  if (key == "lastRow") {
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].devPercentage /= auditGroup.length;
                    mergedAudit.reportGenerated.table1["lastRow"].devTotal /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].naPercentage /= auditGroup.length;
                    mergedAudit.reportGenerated.table1["lastRow"].naTotal /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].stdPercentage /= auditGroup.length;
                    mergedAudit.reportGenerated.table1["lastRow"].stdTotal /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].totalPercentage /= auditGroup.length;
                    mergedAudit.reportGenerated.table1["lastRow"].totalPoints /=
                      auditGroup.length;
                  } else {
                    mergedAudit.reportGenerated.table1[key].devPoints /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[key].naPoints /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[key].score /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[key].stdPoints /=
                      auditGroup.length;
                    mergedAudit.reportGenerated.table1[key].weight /=
                      auditGroup.length;
                  }
                });

                const lastRow = mergedAudit.reportGenerated.table1.lastRow;
                delete mergedAudit.reportGenerated.table1.lastRow;
                const table1 = [
                  ...Object.values(mergedAudit.reportGenerated.table1),
                  lastRow,
                ];
                const table2 = Object.values(
                  mergedAudit.reportGenerated.table2,
                );

                mergedAudit.reportGenerated = normalizeReportGenerated({
                  table1Pillars: table1,
                  table2Pillars: table2,
                });

                return mergedAudit;
              });

              return {
                id: area.id,
                name: area.name,
                colorPalette: branches[0]?.colorPalette,
                standaloneTotalPercentage:
                  getGroupStandaloneTotalPercentage(areaBranches),
                audits,
              };
            });
        } else {
          branches = type.regions.map((region) => {
            let audits = {};
            const regionBranches = branches.filter(
              (branch) => branch.region.id == region.id,
            );

            branches.map((branch) => {
              if (region.id != branch.region.id) return;
              const auditGroup = branch.audits;

              auditGroup.map((audit) => {
                const month = monthNames[new Date(audit.date).getMonth()];
                if (!audits[month]) audits[month] = [];

                audits[month].push(audit);
              });
            });

            audits = Object.keys(audits).map((key) => {
              const auditGroup = audits[key];
              if (auditGroup.length == 1) return auditGroup[0];

              const mergedAudit = {
                id: key,
                date: auditGroup[0].date,
                reportGenerated: { table1: {}, table2: {} },
              };

              auditGroup.map((audit) => {
                audit.reportGenerated.table2.map((row) => {
                  if (!mergedAudit.reportGenerated.table2[row.pillarId])
                    mergedAudit.reportGenerated.table2[row.pillarId] = {
                      pillarId: row.pillarId,
                      description: row.description,
                      referenceCode: row.referenceCode,
                      totals: { dev: 0, na: 0, std: 0, weight: 0 },
                    };
                  mergedAudit.reportGenerated.table2[row.pillarId].totals.dev +=
                    row.totals.dev;
                  mergedAudit.reportGenerated.table2[row.pillarId].totals.na +=
                    row.totals.na;
                  mergedAudit.reportGenerated.table2[row.pillarId].totals.std +=
                    row.totals.std;
                  mergedAudit.reportGenerated.table2[
                    row.pillarId
                  ].totals.weight += row.totals.weight;
                });

                audit.reportGenerated.table1.map((row, index) => {
                  const isLast =
                    index == audit.reportGenerated.table1.length - 1;

                  if (!isLast) {
                    if (!mergedAudit.reportGenerated.table1[row.pillarId]) {
                      mergedAudit.reportGenerated.table1[row.pillarId] = {
                        pillarId: row.pillarId,
                        description: row.description,
                        referenceCode: row.referenceCode,
                        devPoints: 0,
                        naPoints: 0,
                        score: 0,
                        stdPoints: 0,
                        weight: 0,
                      };
                    }

                    mergedAudit.reportGenerated.table1[
                      row.pillarId
                    ].devPoints += row.devPoints;
                    mergedAudit.reportGenerated.table1[row.pillarId].naPoints +=
                      row.naPoints;
                    mergedAudit.reportGenerated.table1[row.pillarId].score +=
                      row.score;
                    mergedAudit.reportGenerated.table1[
                      row.pillarId
                    ].stdPoints += row.stdPoints;
                    mergedAudit.reportGenerated.table1[row.pillarId].weight +=
                      row.weight;
                  } else {
                    if (!mergedAudit.reportGenerated.table1["lastRow"]) {
                      mergedAudit.reportGenerated.table1["lastRow"] = {
                        devPercentage: 0,
                        devTotal: 0,
                        naPercentage: 0,
                        naTotal: 0,
                        stdPercentage: 0,
                        stdTotal: 0,
                        totalPercentage: 0,
                        totalPoints: 0,
                      };
                    }

                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].devPercentage += row.devPercentage;
                    mergedAudit.reportGenerated.table1["lastRow"].devTotal +=
                      row.devTotal;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].naPercentage += row.naPercentage;
                    mergedAudit.reportGenerated.table1["lastRow"].naTotal +=
                      row.naTotal;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].stdPercentage += row.stdPercentage;
                    mergedAudit.reportGenerated.table1["lastRow"].stdTotal +=
                      row.stdTotal;
                    mergedAudit.reportGenerated.table1[
                      "lastRow"
                    ].totalPercentage += row.totalPercentage;
                    mergedAudit.reportGenerated.table1["lastRow"].totalPoints +=
                      row.totalPoints;
                  }
                });
              });

              Object.keys(mergedAudit.reportGenerated.table2).map((key) => {
                mergedAudit.reportGenerated.table2[key].totals.dev /=
                  auditGroup.length;
                mergedAudit.reportGenerated.table2[key].totals.na /=
                  auditGroup.length;
                mergedAudit.reportGenerated.table2[key].totals.std /=
                  auditGroup.length;
                mergedAudit.reportGenerated.table2[key].totals.weight /=
                  auditGroup.length;
              });

              Object.keys(mergedAudit.reportGenerated.table1).map((key) => {
                if (key == "lastRow") {
                  mergedAudit.reportGenerated.table1["lastRow"].devPercentage /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].devTotal /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].naPercentage /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].naTotal /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].stdPercentage /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].stdTotal /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1[
                    "lastRow"
                  ].totalPercentage /= auditGroup.length;
                  mergedAudit.reportGenerated.table1["lastRow"].totalPoints /=
                    auditGroup.length;
                } else {
                  mergedAudit.reportGenerated.table1[key].devPoints /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1[key].naPoints /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1[key].score /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1[key].stdPoints /=
                    auditGroup.length;
                  mergedAudit.reportGenerated.table1[key].weight /=
                    auditGroup.length;
                }
              });

              const lastRow = mergedAudit.reportGenerated.table1.lastRow;
              delete mergedAudit.reportGenerated.table1.lastRow;
              const table1 = [
                ...Object.values(mergedAudit.reportGenerated.table1),
                lastRow,
              ];
              const table2 = Object.values(mergedAudit.reportGenerated.table2);

              mergedAudit.reportGenerated = normalizeReportGenerated({
                table1Pillars: table1,
                table2Pillars: table2,
              });

              return mergedAudit;
            });

            return {
              id: region.id,
              name: region.description,
              colorPalette: branches[0]?.colorPalette,
              standaloneTotalPercentage:
                getGroupStandaloneTotalPercentage(regionBranches),
              audits,
            };
          });
        }
        break;
    }
  }

  const { label } = useLanguage();

  const getPillars = useCallback(() => {
    const pillars = {};
    branches.forEach((branch) => {
      branch.audits.forEach((audit) => {
        const internalPillars =
          audit.reportGenerated?.table2Pillars || audit.reportGenerated?.table2;
        internalPillars?.forEach((internalPillar) => {
          if (!pillars[internalPillar.pillarId])
            pillars[internalPillar.pillarId] = {
              ...internalPillar,
              id: internalPillar.pillarId,
              auditKpis: {},
            };

          internalPillar.auditKpis?.forEach((auditKpi) => {
            pillars[internalPillar.pillarId].auditKpis[auditKpi.referenceCode] =
              auditKpi;
          });
        });
      });
    });

    const arrayPillars = Object.keys(pillars).map((key) => {
      const kpis = Object.keys(pillars[key].auditKpis).map((key2) => {
        return pillars[key].auditKpis[key2];
      });
      return { ...pillars[key], auditKpis: null, kpis };
    });

    arrayPillars.sort((a, b) => a.referenceCode.localeCompare(b.referenceCode));

    return arrayPillars;
  }, [branches]);

  audit = { pillars: getPillars() };

  const pdfRef = useRef(null);

  const { DashboardTable, reportScore } = useMemo(() => {
    const { score, dashboardTableHtml } = isComparative
      ? ComparativeDashboardTableConstruct(audit.pillars, branches)
      : DashboardTableConstruct(audit.pillars, branches);
    return { DashboardTable: dashboardTableHtml, reportScore: score };
  }, [audit, branches, isComparative]);

  const BarChart = useMemo(() => {
    return branches?.length === 1 && !isComparative
      ? BarChartConstruct(audit?.pillars, branches)
      : BarChartConstructv2(audit?.pillars, branches, isComparative);
  }, [audit, branches]);

  const PillarBarChart = useMemo(() => {
    return PillarBarChartConstruct(audit?.pillars, branches, isComparative);
  }, [audit, branches]);

  const PieChart = useMemo(() => {
    return PieChartConstruct(audit?.pillars, branches, isComparative);
  }, [audit, branches]);

  const GeneralGuidelinesTable = useMemo(() => {
    return (
      <GeneralGuidelinesTableConstruct
        audit={audit}
        pillars={audit?.pillars}
        branchGroup={branchGroup}
      />
    );
  }, [audit]);

  useEffect(() => {
    if (branches[0]?.colorPalette) {
      const {
        color1,
        color2,
        color3,
        fontColor1,
        fontColor2,
        fontColor3,
        ...rest
      } = branches[0]?.colorPalette;
      document.documentElement.style.setProperty("--var-color-1", color1);
      document.documentElement.style.setProperty("--var-color-2", color2);
      document.documentElement.style.setProperty("--var-color-3", color3);

      document.documentElement.style.setProperty("--var-font-1", fontColor1);
      document.documentElement.style.setProperty("--var-font-2", fontColor2);
      document.documentElement.style.setProperty("--var-font-3", fontColor3);
    }
    // Add more variable changes as needed
  }, [branches]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "whitesmoke",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
      }}
    >
      <div className={styles["pdf-container"]} ref={pdfRef}>
        {/* cover */}
        <div className={`${styles["pdf-item-container"]} printable`} id="cover">
          <div className={styles["pdf-item"]}>
            <div className={styles["report-cover"]}>
              <Avatar
                className={styles["report-cover-logo"]}
                variant="rounded"
                src={branchGroup?.logo || branchGroup?.branches?.[0]?.logo} //`${UPLOAD_URL}/${branchGroup?.branches?.[0]?.logo}`
              ></Avatar>
              <div className={styles["cover-info-container-box"]}>
                <div className={styles["cover-info-container"]}>
                  <div className={styles["assessment"]}>
                    <span className={styles["deg"]}>360&deg;</span>
                    <span className={styles["text"]}>
                      {label("consolidated-report")}
                    </span>
                  </div>
                  <div>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "start",
                        height: "100%",
                        alignItems: "center",
                        fontSize: "2rem",
                      }}
                    >
                      {branchGroup?.name}
                      {type.value === "region"
                        ? ` - ${type.region.description}`
                        : ""}
                    </span>
                  </div>
                </div>
                <div className={styles["cover-info-rating"]}>
                  <span>
                    <span>{label("rating-singular")}</span>
                    {isNaN(reportScore) ? null : (
                      <img
                        className={`${styles.flag} ${styles.bx} ${styles["bxs-flag"]}}`}
                        src={flagColor(reportScore)}
                        alt="flag-score"
                      />
                    )}
                  </span>
                  <span>{isNaN(reportScore) ? "-" : reportScore + "%"}</span>
                </div>
              </div>
              <div className={styles["cover-confidental"]}>
                <img src={ifpcSticker} alt="ifpc" />
                <img src={confidentialSticker} alt="confidential" />
              </div>
              <div className={styles["cover-footer"]}>
                <span>{label("consolidated-report-rights")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* dashboard table */}
        <div className={styles["draggable-item"]}>
          <div className={`${styles["pdf-item-container"]} title printable`}>
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <div className={styles["title"]}>
                {label("consolidated-dashboard")}
              </div>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
          {!audit.pillars.length && (
            <MDTypography>
              {label("consolidated-empty-pillars") ||
                "All pillars are excluded. The report contains no pillar results."}
            </MDTypography>
          )}
          {DashboardTable}
        </div>

        {/* risk analysis - static table */}
        <div
          className={`${styles["pdf-item-container"]} ${styles["draggable-item"]}`}
        >
          <div className={`${styles["pdf-item-container"]} printable no-title`}>
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className={styles["pdf-item"]}>
                  <div className={styles["subtitle"]}>
                    <div className={styles["line"]}></div>
                    {label("risk-analysis")}
                    <div className={styles["line"]}></div>
                  </div>
                  <div className={styles["risk-analysis-table"]}>
                    <div>
                      <Table
                        theads={[label("risk-range"), label("weight-singular")]}
                        rows={[
                          [label("low-singular"), "1"],
                          [label("moderate-singular"), "2"],
                          [label("critical-singular"), label("above-3")],
                        ]}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["subtitle"]}>
                    <div className={styles["line"]}></div>
                    {label("performance-ratings")}
                    <div className={styles["line"]}></div>
                  </div>
                  <div className={styles["perf-ratings"]}>
                    <div className={styles["perfs"]}>
                      {perfs(label).map((perf, index) => {
                        return (
                          <div
                            className={styles["perf"]}
                            key={"Perf: " + index}
                          >
                            <span>{perf.title}</span>
                            <div className={styles["arrow"]}>
                              <div className={styles["arrow-tail"]}></div>
                              <div className={styles["arrow-head"]}></div>
                            </div>
                            <span>{perf.value}%</span>
                            <img
                              className={`${styles.flag} ${styles.bx} ${styles["bxs-flag"]}}`}
                              src={flagColorBasedOnString(perf.color)}
                              alt="flag-score"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className={styles["perf-levels"]}>
                      <span>{label("low-singular")}</span>
                      <div className={`${styles.arrow} ${styles.vertical}`}>
                        <div className={styles["arrow-tail"]}></div>
                        <div className={styles["arrow-head"]}></div>
                      </div>
                      <span>{label("high-singular")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
        </div>

        {/* General Performance Positioning */}
        <div className={styles["draggable-item"]}>
          <div
            className={`${styles["pdf-item-container"]} printable title`}
            id="title"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <div className={styles["title"]}>
                {label("general-performance-positioning")}
              </div>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>

          <div
            className={`${styles["pdf-item-container"]} printable`}
            id="barchart"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>{BarChart}</div>
            <div className={styles["pdf-margin"]}></div>
          </div>

          <div className={styles["pie-charts"]}>{PieChart}</div>
        </div>

        {/* Specific Performance Positioning */}
        <div className={styles["draggable-item"]}>
          <div
            className={`${styles["pdf-item-container"]} printable title`}
            id="title"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <div className={styles["title"]}>
                {label("specific-performance-positioning")}
              </div>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
          {PillarBarChart}
        </div>

        {/* General Guidelines */}
        <div className={styles["draggable-item"]}>
          <div
            className={`${styles["pdf-item-container"]} printable title`}
            id="guidelines"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <div className={styles["title"]}>
                {label("general-guidelines")}
              </div>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
          {GeneralGuidelinesTable}
        </div>

        <div id="table-hidden-container"></div>
      </div>
    </Box>
  );
}

const Table = ({ theads, rows }) => {
  return (
    <table>
      <thead>
        <tr>
          {theads.map((thead, index) => {
            return <th key={"Thead: " + index}>{thead}</th>;
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, row_index) => {
          return (
            <tr key={"Row: " + row_index}>
              {row.map((item, item_index) => {
                return <td key={"Item: " + item_index}>{item}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const GeneralGuidelinesTableConstruct = ({ pillars, branchGroup }) => {
  const { label } = useLanguage();
  const promiseTrain = useRef({});
  const oldGuidelines = useRef();
  const [guidelines, setGuidelines] = useState([]);

  const debouncedSave = useCallback(
    debounce((nextValue) => upsertGuidelines(nextValue), 3000),
    [],
  );

  function updateGuidelines(pillarId, newValue) {
    setGuidelines((curr) => {
      const newGuidelines = [...curr];
      const index = newGuidelines.findIndex(
        (guideline) => guideline.pillarId == pillarId,
      );

      console.log("Found: ", index);

      if (index == -1) {
        newGuidelines.push({ pillarId, ...newValue });
      } else {
        newGuidelines[index] = { ...newGuidelines[index], ...newValue };
      }

      debouncedSave(newGuidelines);

      console.log("New guidelines: ", newGuidelines);

      return newGuidelines;
    });
  }

  async function upsertGuidelines(guidelines) {
    const promId = Math.random();
    const prom = auditsService.upsertConsolidatedReport(
      branchGroup.id,
      guidelines,
    );
    promiseTrain.current[promId] = prom;

    const [response, data] = await prom;
    delete promiseTrain.current[promId];

    if (response.ok) {
      if (Object.keys(promiseTrain.current).length == 0) {
        oldGuidelines.current = data;
        setGuidelines(data);
      }
    } else {
    }
  }

  async function getGuidelines() {
    const [response, data] = await auditsService.getConsolidatedReport(
      branchGroup.id,
    );
    if (response.ok) {
      const newData = [...data];
      pillars.map((pillar) => {
        const index = data.findIndex((d) => d.pillarId == pillar.pillarId);
        if (index == -1) {
          newData.push({
            pillarId: pillar.pillarId,
            recommendation: "",
            analysis: "",
          });
        }
      });

      setGuidelines(newData);
    }
  }

  useEffect(() => {
    getGuidelines();
  }, []);

  if (!pillars) return null;

  const content = useMemo(() => {
    const c = {};
    guidelines.forEach((element) => {
      c[element.pillarId] = element;
    });

    return c;
  }, [guidelines]);

  return (
    <div
      className={`${styles["pdf-item-container"]} printable`}
      id="table"
      style={{ width: "100% !important" }}
    >
      <div className={styles["pdf-margin"]}></div>
      <div className={styles["pdf-item"]}>
        <table style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th
                className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]}`}
                style={{ width: "5%" }}
              >
                {label("ref-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]}`}
                style={{ width: "15%" }}
              >
                {label("pillar-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]}`}
                style={{ width: "40%" }}
              >
                {label("analysis")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]}`}
                style={{ width: "40%" }}
              >
                {label("instructions-and-recommendations")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pillars
              .filter((pillar) => pillar.totals.weight)
              .map((pillar, index) => {
                return (
                  <tr
                    key={"Guidelines row: " + pillar.id + pillar.referenceCode}
                  >
                    <td
                      className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-10-cell"]}`}
                    >
                      {pillar.referenceCode}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["left-align-cell"]}`}
                      style={{ textAlign: "start", padding: "2px" }}
                    >
                      {pillar.description}
                    </td>

                    <td
                      className={styles["white-cell"]}
                      style={{
                        height: "100%",
                        padding: "2px",
                        verticalAlign: "top",
                      }}
                    >
                      <ResizableTextarea
                        sx={{
                          width: "100%",
                          height: "100%",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "none",
                            },
                            "& textarea": {
                              padding: "0 !important",
                            },
                          },
                          "& .MuiInputBase-input": {
                            outline: "none",
                          },
                        }}
                        value={content[pillar.pillarId]?.analysis || ""}
                        onChange={(e) => {
                          console.log(
                            "Update on: ",
                            pillar.pillarId,
                            " - analysis: ",
                            e.target.value,
                          );
                          updateGuidelines(pillar.pillarId, {
                            analysis: e.target.value,
                          });
                        }}
                      />
                    </td>

                    <td
                      className={styles["white-cell"]}
                      style={{
                        height: "100%",
                        padding: "2px",
                        verticalAlign: "top",
                      }}
                    >
                      <ResizableTextarea
                        sx={{
                          width: "100%",
                          height: "100%",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "none",
                            },
                            "& textarea": {
                              padding: "0 !important",
                            },
                          },
                          "& .MuiInputBase-input": {
                            outline: "none",
                          },
                        }}
                        value={content[pillar.pillarId]?.recommendation || ""}
                        onChange={(e) => {
                          console.log(
                            "Update on: ",
                            pillar.pillarId,
                            " - recommendation: ",
                            e.target.value,
                          );
                          updateGuidelines(pillar.pillarId, {
                            recommendation: e.target.value,
                          });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className={styles["pdf-margin"]}></div>
    </div>
  );
};

const BarChartConstruct = (pillars, branches) => {
  if (!pillars?.length || !branches || !branches?.length) return null;

  const singleBranch = branches?.length === 1 ? branches[0] : false;

  const chartData = branches.map((branch) => {
    return {
      name: branch.name,
      value:
        Math.round(
          branch.audits[branch.audits.length - 1].reportGenerated.table1[
            branch.audits[branch.audits.length - 1].reportGenerated.table1
              .length - 1
          ].totalPercentage * 1000,
        ) / 10,
    };
  });

  const generateData = () => {
    const data = [];
    if (singleBranch) {
      singleBranch.audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        const value = {
          name: monthNames[new Date(audit.date).getMonth()],
          overall:
            Math.round(table1[table1.length - 1].totalPercentage * 1000) / 10,
        };

        data.push(value);
      });
      return data;
    } else {
      branches.map((branch) => {
        const audit = branch.audits[branch.audits.length - 1];
        const { table1 } = audit.reportGenerated;

        const value = {
          name: branch.name,
          overall:
            Math.round(table1[table1.length - 1].totalPercentage * 1000) / 10,
        };

        data.push(value);
      });

      return data;
    }
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;
    const radius = 10;

    return (
      <g>
        <text
          x={x + width / 2}
          y={y - radius}
          fill="#000"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {value == 0 || value ? value + "%" : ""}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveContainer width={"100%"} height={850}>
      <BarChart
        data={generateData()}
        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        //layout="vertical"
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" interval={0} />
        <YAxis
          type="number"
          tick={{ fontSize: "0.8em" }}
          ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
          tickFormatter={(value) => `${value}%`}
          dataKey="overall"
        />
        <Tooltip
          formatter={(value, name, props) => {
            return [`${props.payload.name}: ${value || 0}%`];
          }}
        />
        <Bar dataKey={`overall`} maxBarSize={40}>
          {generateData()?.map((entry, index) => (
            <Cell
              key={`cell-${entry.id}`}
              fill={flagHexColor(Number(entry.overall) || 0)}
            />
          ))}
          <LabelList dataKey="overall" content={renderCustomizedLabel} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const BarChartConstructv2 = (pillars, branches, isComparative) => {
  const { label } = useLanguage();
  if (!pillars?.length || !branches?.length) return null;

  function generateData() {
    const data = [];
    let monthsInBranch = [];
    branches.forEach((branch) => {
      const months = branch.audits
        .map(
          ({ date }) =>
            new Date(date).getMonth() * 0.01 +
            new Date(date).getFullYear() +
            new Date(date).getDate() * 0.0001,
        )
        .map((v) => Number(v.toFixed(4)));
      monthsInBranch = [
        ...monthsInBranch,
        ...months, //.filter((month) => !monthsInBranch.includes(month)),
      ];
    });

    monthsInBranch = monthsInBranch.sort((a, b) => a - b);
    monthsInBranch = Array.from(new Set(monthsInBranch));
    const daysInBranch = [];
    monthsInBranch = monthsInBranch.map((month) => {
      let m = parseInt(month.toFixed(4).split(".")[1].substring(0, 2));
      let ds = month.toFixed(4).split(".")[1].substring(2, 4);
      if (ds.length == 1) ds += "0";

      let d = parseInt(ds);
      daysInBranch.push(d);
      return m;
    });

    branches.map((branch) => {
      const branchValues = [];
      let counter = 0;
      const audits = [...branch.audits].reverse();
      audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        const row = table1[table1.length - 1];

        const value = { name: "" + branch.name };
        value.audit = Math.round(row.totalPercentage * 1000) / 10;
        value.subname = monthNames[new Date(audit.date).getMonth()];
        branchValues.push({
          ...value,
          month: new Date(audit.date).getMonth(),
        });
      });

      monthsInBranch.forEach((month) => {
        const value = branchValues.find(
          (branchValue) => branchValue.month == month,
        );
        if (value) {
          value.name += "___" + counter++;
          data.push(value);
        } else {
          data.push({
            name: branch.name + "___" + counter++,
            audit: "",
            subname: "",
          });
        }
      });
    });

    return data;
  }

  function generateData2() {
    let data = [];
    branches.map((branch) => {
      let reset = 0;
      let branchAudits = [];
      branch.audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        const row = table1[table1.length - 1];
        branchAudits.push({
          name: branch.name,
          audit: Math.round(row.totalPercentage * 1000) / 10,
          subname: isComparative
            ? audit.type == INTERNAL
              ? "Int"
              : "Ext"
            : monthNames[new Date(audit.date).getMonth()].substring(0, 3),
          month: new Date(audit.date).getMonth(),
          date: new Date(audit.date),
          branchId: branch.id,
        });
      });
      if (!isComparative) branchAudits.sort((a, b) => a.date - b.date);
      branchAudits.forEach((_, index) => {
        branchAudits[index].name +=
          "___" + index + "___" + branchAudits[index].branchId;
      });
      data = data.concat(branchAudits);
      reset = 0;
    });

    const subnameGroups = {};
    data.forEach((d, index) => {
      if (!subnameGroups[d.branchId + d.subname])
        subnameGroups[d.branchId + d.subname] = [];
      subnameGroups[d.branchId + d.subname].push({ d, index });
    });

    Object.values(subnameGroups).forEach((arr) => {
      if (arr.length > 1) {
        arr.forEach((e, count) => {
          data[e.index].subname += " " + (count + 1);
        });
      }
    });

    return data;
  }

  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;
    const radius = 10;

    return (
      <g>
        <text
          x={x + width / 2}
          y={y - radius}
          fill="#000"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="9"
        >
          {value === 0 || value ? value + "%" : ""}
        </text>
      </g>
    );
  };

  function getNumberOfBars(branchId) {
    return branches.find((b) => b.id == branchId).audits.length;

    var max = 0;
    branches.forEach((branch) => {
      max = branch.audits.length > max ? branch.audits.length : max;
    });
    return max;
  }

  const renderBranchTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const month = parseInt(value.split("___")[1]);
    const maxNumber = getNumberOfBars(Number(value.split("___")[2]));

    if (maxNumber === 1 || month === Math.floor(maxNumber / 2)) {
      const even = maxNumber % 2 == 0;

      const xValue =
        x + (maxNumber === 1 ? 250 / (branches.length - 1) : !even ? 10 : 0); //-600 / (maxNumber * branches.length)); -750 / (maxNumber * branches.length)

      const yValue = y;
      let rotation = 0;
      if (branches.length > 3) {
        rotation = 30;
      }
      return (
        <text
          x={xValue}
          y={yValue}
          textAnchor="middle"
          fontSize="12"
          transform={`rotate(-${rotation}, ${xValue}, ${yValue})`}
        >
          {value?.split("___")[0]}
        </text>
      );
    }
    if (month === maxNumber || month == 0)
      return <path d={`M${x},${y + 12}v${-51}`} stroke="black" />;
  };

  const renderMonthTick = (tick) => {
    return tick?.split(" ").join("") || "";
  };

  return (
    <ResponsiveContainer width={"100%"} height={392}>
      <BarChart
        data={generateData2()}
        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="subname"
          style={{
            fontSize: "12px",
          }}
          interval={0}
          textAnchor="middle"
          tickFormatter={renderMonthTick}
        />
        <XAxis
          dataKey="name"
          tick={renderBranchTick}
          axisLine={false}
          tickLine={false}
          interval={0}
          scale="band"
          textAnchor="middle"
          xAxisId="quarter"
        />

        <YAxis
          type="number"
          tick={{ fontSize: "0.8em" }}
          ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
          tickFormatter={(value) => `${value}%`}
          dataKey="value"
        />
        <Tooltip
          formatter={(value, name, props) => {
            return [
              `${props.payload.name?.split("___")[0] || name}: ${value || 0}%`,
            ];
          }}
        />
        <Bar dataKey={`audit`} maxBarSize={40}>
          {generateData2()?.map((entry, index) => (
            <Cell
              key={`cell-${entry.id}`}
              fill={flagHexColor(Number(entry.audit) || 0)}
            />
          ))}
          <LabelList dataKey="audit" content={renderCustomizedLabel} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const PillarBarChartConstruct = (pillars, branches, isComparative) => {
  const { label } = useLanguage();
  if (!pillars?.length || !branches?.length) return null;

  function generateData(pillarId) {
    const data = [];
    let monthsInBranch = [];
    branches.forEach((branch) => {
      const months = branch.audits.map(
        ({ date }) =>
          new Date(date).getMonth() * 0.01 + new Date(date).getFullYear(),
      );
      monthsInBranch = [
        ...monthsInBranch,
        ...months.filter((month) => !monthsInBranch.includes(month)),
      ];
    });

    monthsInBranch = monthsInBranch.sort((a, b) => a - b);
    monthsInBranch = monthsInBranch.map((month) =>
      parseInt(month.toFixed(2).split(".")[1]),
    );

    monthsInBranch = Array.from(new Set(monthsInBranch));

    branches.map((branch) => {
      const branchValues = [];
      let counter = 0;
      const audits = [...branch.audits].reverse();
      audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        for (let i = 0; i < table1.length - 1; i++) {
          if (table1[i].pillarId == pillarId) {
            const value = { name: "" + branch.name };
            value.audit = Math.round(table1[i].score * 1000) / 10;
            value.subname = monthNames[new Date(audit.date).getMonth()];
            branchValues.push({
              ...value,
              month: new Date(audit.date).getMonth(),
            });
            //data.push(value);
            //counter++;
          }
        }
      });

      monthsInBranch.forEach((month) => {
        const value = branchValues.find(
          (branchValue) => branchValue.month == month,
        );
        if (value) {
          value.name += "___" + counter++;
          data.push(value);
        } else {
          data.push({
            name: branch.name + "___" + counter++,
            audit: "",
            subname: "",
          });
        }
      });
    });

    return data;
  }

  function generateData2(pillarId) {
    let data = [];
    branches.map((branch) => {
      let reset = 0;
      let branchAudits = [];
      branch.audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        for (let i = 0; i < table1.length - 1; i++) {
          if (table1[i].pillarId == pillarId) {
            branchAudits.push({
              name: branch.name,
              audit: Math.round(table1[i].score * 1000) / 10,
              subname: isComparative
                ? audit.type == INTERNAL
                  ? "Int"
                  : "Ext"
                : monthNames[new Date(audit.date).getMonth()].substring(0, 3),
              month: new Date(audit.date).getMonth(),
              date: new Date(audit.date),
              branchId: branch.id,
            });
          }
        }
      });
      if (!isComparative) branchAudits.sort((a, b) => a.date - b.date);
      branchAudits.forEach((_, index) => {
        branchAudits[index].name +=
          "___" + index + "___" + branchAudits[index].branchId;
      });
      data = data.concat(branchAudits);
      reset = 0;
    });

    const subnameGroups = {};
    data.forEach((d, index) => {
      if (!subnameGroups[d.branchId + d.subname])
        subnameGroups[d.branchId + d.subname] = [];
      subnameGroups[d.branchId + d.subname].push({ d, index });
    });

    Object.values(subnameGroups).forEach((arr) => {
      if (arr.length > 1) {
        arr.forEach((e, count) => {
          data[e.index].subname += " " + (count + 1);
        });
      }
    });

    return data;
  }

  function calculateAverageDiff(pillarId) {
    let averageNew = 0,
      averageOld = 0,
      counterNew = 0,
      counterOld = 0,
      averageDiff = 0;

    let pillarDescription = "";

    branches.forEach((branch) => {
      const audit1 =
        branch.audits.length > 1
          ? branch.audits[branch.audits.length - 2]
          : null;
      const audit2 =
        branch.audits.length > 0
          ? branch.audits[branch.audits.length - 1]
          : null;

      if (audit1 && audit2) {
        const table1 = audit1.reportGenerated.table1;
        const table2 = audit2.reportGenerated.table1;

        const pillarValue1 =
          table1.find((v) => v.pillarId == pillarId)?.score || 0;
        const pillarValue2 =
          table2.find((v) => v.pillarId == pillarId)?.score || 0;

        averageDiff += pillarValue2 - pillarValue1;
      }
    });

    // if (counterNew > 0) averageNew /= branches.length;
    // if (counterOld > 0) averageOld /= branches.length;

    const difference = averageDiff / branches.length;

    let text = (
      <>
        {Math.abs(difference * 100).toFixed(2)}%{" "}
        <span
          style={{
            fontWeight: "bold",
            color:
              difference > 0 ? colors.green : difference < 0 ? colors.red : "",
          }}
        >
          {difference >= 0 ? "↑" : "↓"}
        </span>
      </>
    );

    return {
      value: difference,
      text,
    };
  }

  const getPillars = () => {
    const keptPillars = [];

    branches.forEach((branch) => {
      const averageTable = calculateAverageTable(branch);
      averageTable.forEach((item) => {
        const pillar = pillars.find(
          (pillar) => pillar.pillarId == item.pillarId,
        );
        if (pillar && pillar.totals.weight) {
          keptPillars.push({ pillar, audits: branch.audits });
        }
      });
    });

    const returnedPillars = [];

    keptPillars.map((keptPillar) => {
      const foundPillar = returnedPillars.find(
        (p) => p.pillar.pillarId == keptPillar.pillar.pillarId,
      );
      if (!foundPillar) {
        returnedPillars.push(keptPillar);
      }
    });

    returnedPillars.sort((a, b) =>
      a.pillar.referenceCode.localeCompare(b.pillar.referenceCode),
    );

    return returnedPillars;
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, width, height, value } = props;
    const radius = 10;

    return (
      <g>
        <text
          x={x + width / 2}
          y={y - radius}
          fill="#000"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="9"
        >
          {value === 0 || value ? value + "%" : ""}
        </text>
      </g>
    );
  };

  function getNumberOfBars(branchId) {
    return branches.find((b) => b.id == branchId).audits.length;
    var max = 0;
    branches.forEach((branch) => {
      max = branch.audits.length > max ? branch.audits.length : max;
    });
    return max;
  }

  const renderBranchTick = (tickProps, barCount) => {
    const { x, y, payload, width } = tickProps;
    const { value } = payload;
    const month = parseInt(value.split("___")[1]);
    const maxNumber = getNumberOfBars(Number(value.split("___")[2]));

    const even = maxNumber % 2 == 0;

    const centerOffset = width / barCount / 2;

    let xValue =
      x + (maxNumber === 1 ? 250 / (branches.length - 1) : !even ? 0 : 0); //-750 / (maxNumber * branches.length)); //-600 / (maxNumber * branches.length));

    xValue = x + centerOffset;

    const yValue = y;
    let rotation = 0;
    if (branches.length > 3) {
      rotation = 30;
    }
    if (maxNumber === 1) {
      return (
        <g>
          {/* Centered label */}
          <text
            x={xValue}
            y={yValue}
            textAnchor="middle"
            fontSize="12"
            transform={`rotate(-${rotation}, ${xValue}, ${yValue})`}
          >
            {value?.split("___")[0]}
          </text>
          <path d={`M${x},${y + 12}v${-51}`} stroke="black" />
        </g>
      );
    }
    if (month === maxNumber || month == 0) {
      return (
        <g>
          {/* Centered label */}
          <path d={`M${x},${y + 12}v${-51}`} stroke="black" />
          <text
            x={xValue}
            y={yValue}
            textAnchor="middle"
            fontSize="12"
            transform={`rotate(-${rotation}, ${xValue}, ${yValue})`}
          >
            {value?.split("___")[0]}
          </text>
        </g>
      );
    }
  };

  const renderMonthTick = (tick) => {
    return tick?.split(" ")?.join("") || "";
  };

  return getPillars().map(({ pillar, audits }, index) => {
    const data = generateData2(pillar.id);
    return (
      <div
        className={`${styles["pdf-item-container"]} printable`}
        id="barchart"
        key={"pillar for pillarChart: " + index}
      >
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]}>
          <h3
            style={{ textAlign: "center" }}
          >{`${pillar.referenceCode}. ${pillar.description}`}</h3>
          <ResponsiveContainer width={"100%"} height={392}>
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              //layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="subname"
                style={{
                  fontSize: "12px",
                }}
                interval={0}
                textAnchor="middle"
                tickFormatter={renderMonthTick}
              />
              <XAxis
                dataKey="name"
                tick={(params) => renderBranchTick(params, data.length)}
                axisLine={false}
                tickLine={false}
                interval={0}
                scale="band"
                textAnchor="middle"
                xAxisId="quarter"
              />

              <YAxis
                type="number"
                tick={{ fontSize: "0.8em" }}
                ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                tickFormatter={(value) => `${value}%`}
                dataKey="value"
              />
              <Tooltip
                formatter={(value, name, props) => {
                  return [
                    `${props.payload.name?.split("___")[0] || name}: ${
                      value || 0
                    }%`,
                  ];
                }}
              />
              <Bar dataKey={`audit`} maxBarSize={40}>
                {generateData2(pillar.id)?.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.id}`}
                    fill={flagHexColor(Number(entry.audit) || 0)}
                  />
                ))}
                <LabelList dataKey="audit" content={renderCustomizedLabel} />
              </Bar>
              {/*Array.from({ length: getNumberOfBars() }).map((_, index) => (
                <Bar
                  key={`audit`}
                  dataKey={`audit`}
                  fill={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                >
                   <LabelList
                    dataKey={`Subname ${index}`}
                    content={renderCustomizedLabel}
                  />
                </Bar>
              ))*/}
            </BarChart>
          </ResponsiveContainer>
          <h4 style={{ textAlign: "center" }}>
            {label("average-singular")}={calculateAverageDiff(pillar.id).text}
          </h4>
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    );
  });
};

const PieChartConstruct = (pillars, branches, isComparative) => {
  const { label } = useLanguage();
  if (!pillars?.length || !branches || !branches?.length) return null;
  const singleBranch =
    branches?.length === 1 ? (isComparative ? false : branches[0]) : false;

  const constructBranchPieData = (branchesData) => {
    if (singleBranch) {
      const branchPieArray = singleBranch.audits.map((audit, index) => {
        return {
          name: monthNames[new Date(audit.date).getMonth()],
          value:
            Math.round(
              audit.reportGenerated.table1[
                audit.reportGenerated.table1.length - 1
              ]?.totalPercentage * 1000,
            ) / 10,
          color: flagHexColor(
            Math.round(
              audit.reportGenerated.table1[
                audit.reportGenerated.table1.length - 1
              ]?.totalPercentage * 100,
            ) || 0,
          ),
        };
      });

      return branchPieArray;
    } else {
      const branchPieArray = branchesData.map((branch, index) => {
        const lastAudit = branch.audits[branch.audits.length - 1];
        const prevLastAudit = branch.audits[branch.audits.length - 2];
        const averageTable = calculateAverageTable(branch);
        return {
          name: branch.name,
          value:
            Math.round(
              averageTable[averageTable.length - 1].totalPercentage * 1000,
            ) / 10,
          internalValue: prevLastAudit
            ? Math.round(getAuditTotalPercentage(prevLastAudit) * 1000) / 10
            : 0,
          externalValue:
            Math.round(
              lastAudit.reportGenerated.table1[
                lastAudit.reportGenerated.table1.length - 1
              ]?.totalPercentage * 1000,
            ) / 10,
          footer: prevLastAudit ? (
            <span>
              {label("overall-performance")}{" "}
              {lastAudit.reportGenerated.table1[
                lastAudit.reportGenerated.table1.length - 1
              ]?.totalPercentage >
              prevLastAudit.reportGenerated.table1[
                prevLastAudit.reportGenerated.table1.length - 1
              ]?.totalPercentage ? (
                <span
                  style={{ color: "green", fontSize: "30px", fontWeight: 900 }}
                >
                  ↑
                </span>
              ) : (
                <span
                  style={{ color: "red", fontSize: "30px", fontWeight: 900 }}
                >
                  ↓
                </span>
              )}
            </span>
          ) : (
            ""
          ),
          color: flagHexColor(
            Math.round(
              averageTable[averageTable.length - 1]?.totalPercentage * 100,
            ) || 0,
          ),

          internalColor: branch.audits[1]
            ? flagHexColor(
                Math.round(getAuditTotalPercentage(branch.audits[0]) * 100) ||
                  0,
              )
            : "",

          externalColor: branch.audits[1]
            ? flagHexColor(
                Math.round(getAuditTotalPercentage(branch.audits[1]) * 100) ||
                  0,
              )
            : "",
        };
      });
      return branchPieArray;
    }
  };

  const dataPie = constructBranchPieData(branches);

  const pies = dataPie.map((pie, index) => {
    const { name, value, color, footer } = pie;
    const data = value;
    const title = name;
    const keyProp = "Pie: " + index;
    const pieChartStyle = (data, color) => ({
      background: `conic-gradient(from 0deg, ${color} 0%, ${color} ${data}%, #98958e ${data}%, #98958e 100%)`,
    });
    return (
      <div className={`${styles["pie-chart"]}`} key={keyProp}>
        <div className={styles["pie-title"]} style={{ textAlign: "center" }}>
          {title}
        </div>
        <div
          className={styles["pie-chart-circle"]}
          style={{
            ...pieChartStyle(
              isComparative ? pie.externalValue : data,
              isComparative ? pie.externalColor : color,
            ),
            position: "relative",
          }}
        >
          {isComparative ? (
            <div
              className={styles["pie-chart-circle"]}
              style={{
                ...pieChartStyle(pie.internalValue, pie.internalColor),
                width: "70%",
                border: "1px solid white",
              }}
            >
              <div
                className={styles["pie-center-value"]}
                style={{ width: "50%", height: "50%" }}
              >
                <h2
                  style={{
                    position: "absolute",
                    top: 0,
                    fontSize: "20px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {pie.externalValue?.toFixed?.(1)?.replace("100.0", "100")}%
                  <span style={{ fontSize: "12px" }}>(EXT)</span>
                </h2>
                <h2
                  style={{
                    position: "absolute",
                    top: "34px",
                    fontSize: "20px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {pie.internalValue?.toFixed?.(1)?.replace("100.0", "100")}%
                  <span style={{ fontSize: "12px" }}>(INT)</span>
                </h2>
              </div>
            </div>
          ) : (
            <h1 className={styles["pie-center-value"]}>
              {data?.toFixed?.(1)?.replace("100.0", "100")}%
            </h1>
          )}
        </div>
        <div className={styles["pie-footer"]}>{footer}</div>
      </div>
    );
  });

  return pies.map((pie, index) => {
    if (index % 2 == 0) {
      return (
        <div
          className={`${styles["pdf-item-container"]} printable`}
          id="piechart"
          key={"pillar for pieChart: " + index}
        >
          <div className={styles["pdf-item"]}>
            {pie}
            {index < pies.length ? pies[index + 1] : null}
          </div>
        </div>
      );
    }
  });
};

const DashboardTableConstruct = (pillars, branches, isComparative) => {
  const { label } = useLanguage();
  if (!branches || !pillars)
    return {
      dashboardTableHtml: null,
      score: 0,
    };

  const singleBranch = isComparative
    ? false
    : branches?.length === 1
      ? branches[0]
      : false;

  function calculateBranchValues(branch) {
    if (!branch) return;
    const averageTable = calculateAverageTable(branch);
    if (!averageTable) return;
    const values = {};
    for (let i = 0; i < averageTable.length - 1; i++) {
      values[averageTable[i].pillarId] = {
        name: branch.name, // table1[i].description
        value: (averageTable[i].score * 100).toFixed(2),
        pillar: averageTable[i],
        totals: averageTable[averageTable.length - 1],
        ignore: averageTable[i].ignore,
      };
    }

    return values;
  }

  function calculateAuditValues(audit) {
    const table1 = audit.reportGenerated?.table1;
    const values = {};
    for (let i = 0; i < table1?.length - 1; i++) {
      values[table1[i].pillarId] = {
        name: monthNames[new Date(audit.date).getMonth()], // table1[i].description
        value: (table1[i].score * 100).toFixed(2),
        pillar: table1[i],
        totals: table1[table1.length - 1],
        ignore: table1[i].ignore,
      };
    }

    return values;
  }
  function calculatePillarValues() {
    const answer = {};

    if (singleBranch) {
      singleBranch.audits.map((audit) => {
        const values = calculateAuditValues(audit);
        Object.keys(values).forEach((key) => {
          if (!answer[key])
            answer[key] = {
              pillar: values[key].pillar,
              average: 0,
              count: 0,
              audits: {},
            };
          answer[key].audits[audit.id] = values[key];

          if (
            (values[key].value || values[key].value == 0) &&
            !values[key].ignore
          ) {
            answer[key].average += Number(values[key].value);
            answer[key].count++;
          }
        });
      });
      Object.keys(answer).map((key) => {
        answer[key].average /= answer[key].count;
      });

      return answer;
    }

    branches.forEach((branch) => {
      const values = calculateBranchValues(branch);
      Object.keys(values).forEach((key) => {
        if (!answer[key])
          answer[key] = {
            pillar: values[key].pillar,
            average: 0,
            count: 0,
            branches: {},
          };
        answer[key].branches[branch.id] = values[key];

        if (
          (values[key].value || values[key].value == 0) &&
          !values[key].ignore
        ) {
          answer[key].average += Number(values[key].value);
          answer[key].count++;
        }
      });
    });

    Object.keys(answer).map((key) => {
      answer[key].average /= answer[key].count;
    });

    return answer;
  }

  let totalWeights = 0;

  const pillarValues = calculatePillarValues();

  function calculateAverage() {
    let average = 0;

    if (singleBranch) {
      singleBranch.audits.map((audit) => {
        const { table1 } = audit.reportGenerated;

        average += table1[table1.length - 1].totalPercentage;
      });

      return ((average * 100) / singleBranch.audits.length).toFixed(2);
    } else {
      branches.map((branch) => {
        const averageTable = calculateAverageTable(branch);

        average += averageTable[averageTable.length - 1].totalPercentage;
      });
      return ((average * 100) / branches.length).toFixed(2);
    }
  }
  return {
    dashboardTableHtml: (
      <div
        className={`${styles["pdf-item-container"]} printable`}
        id="table-scrollable"
      >
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]} style={{ overflowX: "scroll" }}>
          <table>
            <thead>
              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  style={{ width: 50 }}
                  rowSpan={2}
                >
                  {label("ref-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={2}
                  style={{ width: "120px" }}
                >
                  {label("pillar-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={2}
                >
                  {label("weight-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  colSpan={
                    singleBranch ? singleBranch.audits.length : branches.length
                  }
                >
                  {label("score-singular")} %
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={2}
                >
                  {label("average-singular")}
                </th>
              </tr>
              <tr>
                {singleBranch
                  ? singleBranch.audits.map((audit) => {
                      return (
                        <th
                          className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                          style={{ fontSize: "14px" }}
                        >
                          {monthNames[new Date(audit.date).getMonth()]}
                        </th>
                      );
                    })
                  : branches.map((branch) => (
                      <th
                        className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                        style={{
                          fontSize: "14px",
                          minWidth: "300px !important",
                        }}
                      >
                        {branch.name}
                      </th>
                    ))}
              </tr>
            </thead>
            <tbody>
              {pillarValues &&
                branches &&
                pillars &&
                Object.keys(pillarValues).map((pillarId) => {
                  totalWeights += pillarValues[pillarId]?.pillar?.weight;
                  return (
                    <tr key={"Row: " + pillarId}>
                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {pillarValues[pillarId]?.pillar?.referenceCode}
                      </td>
                      <td
                        className={`${styles["color-3-cell"]} ${styles["left-align-cell"]}`}
                      >
                        {pillarValues[pillarId]?.pillar?.description}
                      </td>
                      <td
                        className={styles["color-2-cell"]}
                        style={{ width: "30px" }}
                      >
                        {pillarValues[pillarId]?.pillar?.weight
                          .toFixed(2)
                          .replace(".00", "")}
                      </td>
                      {!singleBranch &&
                        branches.map((branch, index) => {
                          return (
                            <td
                              key={"td of branch:" + branch?.id + " - " + index}
                              className={styles["color-3-cell"]}
                              style={{ minWidth: "300px !important" }}
                            >
                              {pillarValues[pillarId].branches[branch.id]?.value
                                ? pillarValues[pillarId].branches[branch.id]
                                    ?.ignore
                                  ? "—"
                                  : pillarValues[pillarId].branches[branch.id]
                                      ?.value + "%"
                                : "—"}
                            </td>
                          );
                        })}

                      {singleBranch
                        ? singleBranch.audits.map((audit, index) => {
                            return (
                              <td
                                key={"td of audit:" + audit?.id + " - " + index}
                                className={styles["color-3-cell"]}
                              >
                                {pillarValues[pillarId].audits[audit.id]?.value
                                  ? pillarValues[pillarId].audits[audit.id]
                                      ?.ignore
                                    ? "—"
                                    : pillarValues[pillarId].audits[audit.id]
                                        ?.value + "%"
                                  : (0).toFixed(2) + "%"}
                              </td>
                            );
                          })
                        : null}

                      <td
                        className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {pillarValues[pillarId]?.average ||
                        pillarValues[pillarId]?.average == 0
                          ? pillarValues[pillarId]?.average.toFixed(2) + "%"
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr>
                <td className={styles["color-1-cell"]} rowSpan={2}></td>
                <td className={styles["color-3-cell"]}>
                  {label("total-singular")}
                </td>
                <td className={styles["color-2-cell"]}>
                  {totalWeights.toFixed(2).replace(".00", "")}
                </td>
                {!singleBranch &&
                  branches.map((branch, index) => {
                    const values = calculateBranchValues(branch);
                    return (
                      <td
                        key={"td2 of branch:" + branch?.id + " - " + index}
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                        rowSpan={2}
                      >
                        {(
                          calculateAverageTable(branch).at(-1).totalPercentage *
                          100
                        ).toFixed(2)}
                        %
                      </td>
                    );
                  })}

                {singleBranch
                  ? singleBranch.audits.map((audit, index) => {
                      const values = calculateAuditValues(audit);
                      return (
                        <td
                          key={"td2 of audit:" + audit?.id + " - " + index}
                          className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                          rowSpan={2}
                        >
                          {(getAuditTotalPercentage(audit) * 100).toFixed(2)}%
                        </td>
                      );
                    })
                  : null}
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  rowSpan={2}
                >
                  {calculateAverage()}%
                </td>
              </tr>
              <tr>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {label("total-singular")} %
                </td>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    ),
    score: calculateAverage(),
  };
};

const ComparativeDashboardTableConstruct = (pillars, branches) => {
  const { label } = useLanguage();
  if (!branches || !pillars)
    return {
      dashboardTableHtml: null,
      score: 0,
    };

  const singleBranch = branches?.length === 1 ? branches[0] : false;

  function calculateBranchValues(branch, index) {
    if (!branch) return;
    const { table1 } =
      branch.audits[index ?? branch.audits.length - 1]?.reportGenerated;
    if (!table1) return;
    const values = {};
    for (let i = 0; i < table1.length - 1; i++) {
      values[table1[i].pillarId] = {
        name: branch.name, // table1[i].description
        value: (table1[i].score * 100).toFixed(2),
        pillar: table1[i],
        totals: table1[table1.length - 1],
        ignore: table1[i].ignore,
      };
    }

    return values;
  }

  function calculatePillarValues() {
    const internalAnswer = {};
    const externalAnswer = {};

    branches.forEach((branch) => {
      const valuesExternal = calculateBranchValues(branch),
        valuesInternal = calculateBranchValues(branch, 0);
      Object.keys(valuesExternal).forEach((key) => {
        if (!externalAnswer[key])
          externalAnswer[key] = {
            pillar: valuesExternal[key].pillar,
            average: 0,
            count: 0,
            branches: {},
          };
        externalAnswer[key].branches[branch.id] = valuesExternal[key];

        if (
          (valuesExternal[key].value || valuesExternal[key].value == 0) &&
          !valuesExternal[key].ignore
        ) {
          externalAnswer[key].average += Number(valuesExternal[key].value);
          externalAnswer[key].count++;
        }
      });

      Object.keys(valuesInternal).forEach((key) => {
        if (!internalAnswer[key])
          internalAnswer[key] = {
            pillar: valuesInternal[key].pillar,
            average: 0,
            count: 0,
            branches: {},
          };
        internalAnswer[key].branches[branch.id] = valuesInternal[key];

        if (
          (valuesInternal[key].value || valuesInternal[key].value == 0) &&
          !valuesInternal[key].ignore
        ) {
          internalAnswer[key].average += Number(valuesInternal[key].value);
          internalAnswer[key].count++;
        }
      });
    });

    Object.keys(externalAnswer).map((key) => {
      externalAnswer[key].average /= externalAnswer[key].count;
    });

    Object.keys(internalAnswer).map((key) => {
      internalAnswer[key].average /= internalAnswer[key].count;
    });

    return { externalAnswer, internalAnswer };
  }

  function calculateAverage() {
    let average = 0;

    branches.map((branch) => {
      let localAverage = 0;
      let { table1 } = branch.audits[branch.audits.length - 1].reportGenerated;

      localAverage += table1[table1.length - 1].totalPercentage;

      table1 = branch.audits[0].reportGenerated?.table1;

      localAverage += table1[table1.length - 1].totalPercentage;

      localAverage /= 2;

      average += localAverage;
    });
    return ((average * 100) / branches.length).toFixed(2);
  }

  const pillarValues = calculatePillarValues();
  let totalWeights = 0;

  return {
    dashboardTableHtml: (
      <div
        className={`${styles["pdf-item-container"]} printable`}
        id="table-scrollable"
      >
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]} style={{ overflowX: "scroll" }}>
          <table>
            <thead>
              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  style={{ width: 50 }}
                  rowSpan={singleBranch ? 2 : 3}
                >
                  {label("ref-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={singleBranch ? 2 : 3}
                  style={{ width: "120px" }}
                >
                  {label("pillar-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={singleBranch ? 2 : 3}
                >
                  {label("weight-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  colSpan={branches.length * 3}
                >
                  {label("score-singular")} %
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                  rowSpan={singleBranch ? 2 : 3}
                >
                  {label("average-singular")}
                </th>
              </tr>
              {!singleBranch ? (
                <tr>
                  {branches.map((branch) => {
                    return (
                      <th
                        key={"Branch comp: " + branch.id}
                        className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                        colSpan={3}
                      >
                        {branch.name}
                      </th>
                    );
                  })}
                </tr>
              ) : null}
              <tr>
                {branches.map((branch) => {
                  return (
                    <Fragment key={"Branch comps: " + branch.id}>
                      <th
                        className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                      >
                        {label("internal")}
                      </th>
                      <th
                        className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                      >
                        {label("external")}
                      </th>
                      <th
                        className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                      >
                        {label("gap-singular")}
                      </th>
                    </Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pillarValues &&
                branches &&
                pillars &&
                Object.keys(pillarValues.externalAnswer).map((pillarId) => {
                  totalWeights +=
                    pillarValues.externalAnswer?.[pillarId]?.pillar?.weight;
                  return (
                    <tr key={"Row: " + pillarId}>
                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {
                          pillarValues.externalAnswer?.[pillarId]?.pillar
                            ?.referenceCode
                        }
                      </td>
                      <td
                        className={`${styles["color-3-cell"]} ${styles["left-align-cell"]}`}
                      >
                        {
                          pillarValues.externalAnswer?.[pillarId]?.pillar
                            ?.description
                        }
                      </td>
                      <td
                        className={styles["color-2-cell"]}
                        style={{ width: "30px" }}
                      >
                        {pillarValues.externalAnswer?.[pillarId]?.pillar?.weight
                          .toFixed(2)
                          .replace(".00", "")}
                      </td>
                      {branches.map((branch, index) => {
                        const gap = Math.abs(
                          (Number(
                            pillarValues?.internalAnswer?.[pillarId]?.branches[
                              branch.id
                            ]?.value,
                          ) || 0) -
                            (Number(
                              pillarValues?.externalAnswer?.[pillarId]
                                ?.branches[branch.id]?.value,
                            ) || 0),
                        );
                        return (
                          <Fragment
                            key={"td of branch:" + branch?.id + " - " + index}
                          >
                            <td
                              className={styles["color-3-cell"]}
                              style={{ minWidth: "300px !important" }}
                            >
                              {pillarValues?.internalAnswer?.[pillarId]
                                ?.branches[branch.id]?.value
                                ? pillarValues?.internalAnswer?.[pillarId]
                                    ?.branches[branch.id]?.ignore
                                  ? "—"
                                  : pillarValues?.internalAnswer?.[pillarId]
                                      ?.branches[branch.id]?.value + "%"
                                : "—"}
                            </td>
                            <td
                              className={styles["color-3-cell"]}
                              style={{ minWidth: "300px !important" }}
                            >
                              {pillarValues?.externalAnswer?.[pillarId]
                                ?.branches[branch.id]?.value
                                ? pillarValues?.externalAnswer?.[pillarId]
                                    ?.branches[branch.id]?.ignore
                                  ? "—"
                                  : pillarValues?.externalAnswer?.[pillarId]
                                      ?.branches[branch.id]?.value + "%"
                                : "—"}
                            </td>

                            <td
                              className={styles["color-3-cell"]}
                              style={{
                                minWidth: "300px !important",
                                backgroundColor: gapColors(gap),
                                color: "black",
                              }}
                            >
                              {gap.toFixed(2)}%
                            </td>
                          </Fragment>
                        );
                      })}

                      <td
                        className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {isNull(
                          pillarValues.externalAnswer[pillarId]?.average,
                        ) ||
                        isNull(pillarValues.internalAnswer[pillarId]?.average)
                          ? "—"
                          : (
                              (pillarValues.externalAnswer[pillarId]?.average +
                                pillarValues.internalAnswer[pillarId]
                                  ?.average) /
                              2
                            ).toFixed(2) + "%"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            <tfoot>
              <tr>
                <td className={styles["color-1-cell"]} rowSpan={2}></td>
                <td className={styles["color-3-cell"]}>
                  {label("total-singular")}
                </td>
                <td className={styles["color-2-cell"]}>
                  {totalWeights.toFixed(2).replace(".00", "")}
                </td>
                {branches.map((branch, index) => {
                  const internalValues = calculateBranchValues(branch, 0);
                  const externalValues = calculateBranchValues(branch, 1);
                  const gap =
                    Math.abs(
                      getAuditTotalPercentage(branch.audits[1]) -
                        getAuditTotalPercentage(branch.audits[0]),
                    ) * 100;

                  return (
                    <Fragment
                      key={"td2 of branch:" + branch?.id + " - " + index}
                    >
                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                        rowSpan={2}
                      >
                        {(
                          getAuditTotalPercentage(branch.audits[0]) * 100
                        ).toFixed(2)}
                        %
                      </td>

                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                        rowSpan={2}
                      >
                        {(
                          getAuditTotalPercentage(branch.audits[1]) * 100
                        ).toFixed(2)}
                        %
                      </td>
                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                        rowSpan={2}
                        style={{
                          backgroundColor: gapColors(gap),
                          color: "black",
                        }}
                      >
                        {gap.toFixed(2)}%
                      </td>
                    </Fragment>
                  );
                })}
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  rowSpan={2}
                >
                  {calculateAverage()}%
                </td>
              </tr>
              <tr>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {label("total-singular")} %
                </td>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    ),
    score: calculateAverage(),
  };
};

function calculateAverageTable(branch) {
  const averageTable = [];
  let totalWeight = 0;

  branch.audits.forEach((audit) => {
    const { table1 } = audit.reportGenerated;
    for (let i = 0; i < table1.length - 1; i++) {
      if (table1[i].description == "Delivery") if (table1[i].ignore) continue;
      let oldValueIndex = averageTable.findIndex(
        (v) => v.pillarId == table1[i].pillarId,
      );
      if (oldValueIndex == -1) {
        averageTable.push({
          score: 0,
          pillarId: table1[i].pillarId,
          description: table1[i].description,
          referenceCode: table1[i].referenceCode,
          weight: table1[i].weight,
          ignore: table1[i].ignore,
          count: 0,
        });
        oldValueIndex = averageTable.length - 1;

        totalWeight += table1[i].weight;
      }

      averageTable[oldValueIndex].score += table1[i].score;
      averageTable[oldValueIndex].count++;
      averageTable[oldValueIndex].ignore &= table1[i].ignore;
    }
  });

  for (let i = 0; i < averageTable.length; i++) {
    averageTable[i].score /= averageTable[i].count;
  }

  averageTable.push({ totalPercentage: 0, weight: totalWeight });
  branch.audits.forEach((audit) => {
    const { table1 } = audit.reportGenerated;
    const lastRow = table1[table1.length - 1];

    averageTable[averageTable.length - 1].totalPercentage +=
      safeNumber(lastRow.totalPercentage) / branch.audits.length;
  });

  if (Number.isFinite(Number(branch?.standaloneTotalPercentage))) {
    averageTable[averageTable.length - 1].totalPercentage = safeNumber(
      branch.standaloneTotalPercentage,
    );
  }

  return averageTable;
}

function isNull(value) {
  if (value || value == 0) {
    return false;
  }
  return true;
}

function gapColors(gap) {
  if (gap <= 5) return green[300];
  if (gap <= 10) return yellow[300];
  return red[300];
}
